/* WP-10 My Humidor home (§14.5.1): greeting, one situation card by
 * priority, announcement banner, collection snapshot, agreement row;
 * desktop adds a Recent column. */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { portalApi } from '../api/portal-api';
import type { HomeResponse, PortalPickupShop } from '../api/types';
import { useSession } from '../auth/session-context';
import { Banner, FactRow, PrimaryButton, SectionHeader, TextLink } from '../components/primitives';
import { HistoryTimelineEntry } from '../components/timeline';
import { IdCardIcon, InfoIcon, LocationPinIcon, WarningIcon } from '../icons';
import { HumidorCabinetIllustration } from '../illustrations';
import { cn } from '../utils/cn';
import { activeAnnouncement, mediaUrl, shopProfile, useAppearance } from '../appearance/appearance';
import { currentPortalLocale } from '../i18n';
import { formatDate, formatDateOverline } from '../utils/dates';
import { errorText } from '../utils/error-text';
import { historySentence } from '../utils/history-sentence';

/* F3-B — explicit Home states: loading never claims the account is
 * empty, an initial failure replaces the skeleton with an honest error +
 * retry (no fabricated counts), and a failed REFRESH keeps the last
 * confirmed data instead of silently zeroing the screen. */
type HomeState =
  | { status: 'loading' }
  | { status: 'ready'; data: HomeResponse; refreshError: string | null }
  | { status: 'error'; message: string };

export function HomePage() {
  const { t } = useTranslation();
  const locale = currentPortalLocale();
  const { customer } = useSession();
  const navigate = useNavigate();
  useAppearance(); // re-render when the published bundle lands/changes
  const [homeState, setHomeState] = useState<HomeState>({ status: 'loading' });
  const homeRef = useRef(homeState);
  homeRef.current = homeState;
  const [refreshing, setRefreshing] = useState(false);
  const [shops, setShops] = useState<Map<string, PortalPickupShop>>(new Map());
  const [dismissedBanner, setDismissedBanner] = useState(false);
  const homeSeq = useRef(0);
  const inflight = useRef(false);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  /* /home is the required payload; /pickup-shops is OPTIONAL display
   * metadata — its failure degrades to an empty shop map and can never
   * erase a valid Home response. `homeSeq` drops obsolete responses after
   * navigation or when a newer request started; `inflight` blocks
   * overlapping retries. */
  const load = useCallback(async () => {
    if (inflight.current) return;
    inflight.current = true;
    const my = ++homeSeq.current;
    const retained = homeRef.current.status === 'ready' ? homeRef.current.data : null;
    if (retained) {
      setRefreshing(true);
      setHomeState({ status: 'ready', data: retained, refreshError: null });
    } else {
      setHomeState({ status: 'loading' });
    }
    try {
      const [h, s] = await Promise.all([
        portalApi.get<HomeResponse>('/home'),
        portalApi
          .get<{ items: PortalPickupShop[] }>('/pickup-shops')
          .catch(() => ({ items: [] as PortalPickupShop[] })),
      ]);
      if (my !== homeSeq.current || !mountedRef.current) return;
      setShops(new Map(s.items.map((x) => [x.id, x])));
      setHomeState({ status: 'ready', data: h, refreshError: null });
    } catch (err) {
      if (my !== homeSeq.current || !mountedRef.current) return;
      const msg = errorText(err, t);
      // A failed refresh keeps confirmed data; only a failed INITIAL load
      // falls into the dedicated error state.
      setHomeState((prev) =>
        prev.status === 'ready'
          ? { status: 'ready', data: prev.data, refreshError: msg }
          : { status: 'error', message: msg },
      );
    } finally {
      inflight.current = false;
      if (my === homeSeq.current && mountedRef.current) setRefreshing(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const home = homeState.status === 'ready' ? homeState.data : null;
  const loading = homeState.status === 'loading';
  const hardError = homeState.status === 'error' ? homeState.message : null;
  const refreshError = homeState.status === 'ready' ? homeState.refreshError : null;
  const announcement = activeAnnouncement(locale);
  const situation = home?.situation ?? null;
  const readyShop = situation?.type === 'READY' ? findShop(shops, situation.shopName) : null;
  const heroUrl = readyShop ? mediaUrl(shopProfile(readyShop.id)?.heroAssetId) : null;

  return (
    <>
      <p className="hp-label">{formatDateOverline(new Date(), locale)}</p>
      <h1 className="hp-display-xl">
        {t('home.greeting', {
          part: t(`home.${daypart()}`),
          name: customer?.displayName ?? '',
        })}
      </h1>

      {hardError ? (
        <div>
          <p className="hp-danger-text" role="alert" data-quality-id="portal.home.loadError">
            {hardError}
          </p>
          <TextLink onClick={load} disabled={refreshing} data-quality-id="portal.home.retry">
            {t('common.retry')}
          </TextLink>
        </div>
      ) : null}

      {refreshError ? (
        <div>
          <p className="hp-danger-text" role="alert" data-quality-id="portal.home.refreshError">
            {refreshError}
          </p>
          <TextLink onClick={load} disabled={refreshing} data-quality-id="portal.home.retry">
            {t('common.retry')}
          </TextLink>
        </div>
      ) : null}

      {refreshing && !refreshError ? (
        <p
          className="hp-field__hint"
          role="status"
          aria-busy="true"
          data-quality-id="portal.home.updating"
        >
          {t('home.updating')}
        </p>
      ) : null}

      {situation ? (
        <SituationCard situation={situation} shop={readyShop} heroUrl={heroUrl} locale={locale} />
      ) : null}

      {announcement && !dismissedBanner ? (
        <Banner
          style={announcement.style === 'notice' ? 'notice' : 'info'}
          icon={<InfoIcon size={18} />}
          onDismiss={() => setDismissedBanner(true)}
          link={
            announcement.linkUrl
              ? {
                  label: announcement.linkLabel ?? t('common.open'),
                  onClick: () => navigate(announcement.linkUrl!),
                }
              : null
          }
        >
          {announcement.body}
        </Banner>
      ) : null}

      <div className="hp-grid-2">
        <section className="hp-section">
          <SectionHeader title={t('home.snapshot.title')} />
          {home ? (
            <Link to="/humidor" className="hp-row" style={{ textDecoration: 'none' }}>
              <HumidorCabinetIllustration title={t('humidor.title')} width={70} />
              <div>
                <p className="hp-display-l hp-num">{home.snapshot.boxes}</p>
                <p className="hp-muted">
                  {t('home.snapshot.boxes', { count: home.snapshot.boxes })}
                  {home.snapshot.looseCigars > 0
                    ? ` · ${t('home.snapshot.plusLoose', { count: home.snapshot.looseCigars })}`
                    : ''}
                </p>
              </div>
            </Link>
          ) : loading ? (
            <div className="hp-skel" style={{ height: 90 }} />
          ) : null}
          {home?.snapshot.topBrands.length ? (
            <ul className="hp-rows" style={{ listStyle: 'none', padding: 0, marginTop: 16 }}>
              {home.snapshot.topBrands.slice(0, 4).map((b) => (
                <li
                  key={b.brandId ?? 'none'}
                  className="hp-row hp-row--spread"
                  style={{ padding: '8px 0' }}
                >
                  <span>{b.name}</span>
                  <span className="hp-muted">{t('common.boxes', { count: b.boxes })}</span>
                </li>
              ))}
              {home.snapshot.otherBrandsBoxes > 0 ? (
                <li className="hp-row hp-row--spread" style={{ padding: '8px 0' }}>
                  <span className="hp-tertiary">{t('home.snapshot.otherBrands')}</span>
                  <span className="hp-muted">
                    {t('common.boxes', { count: home.snapshot.otherBrandsBoxes })}
                  </span>
                </li>
              ) : null}
            </ul>
          ) : null}
          <TextLink onClick={() => navigate('/humidor')}>{t('home.snapshot.cta')}</TextLink>
        </section>

        <section className="hp-section">
          <SectionHeader title={t('home.recent.title')} />
          {home?.recent?.length ? (
            <div className="hp-timeline">
              {home.recent.slice(0, 3).map((e) => (
                <HistoryTimelineEntry
                  key={e.id}
                  entry={e}
                  sentence={historySentence(e, t)}
                  locale={locale}
                />
              ))}
            </div>
          ) : home ? (
            /* Genuine empty state — only after a successful empty answer. */
            <p className="hp-tertiary">{t('home.recent.empty')}</p>
          ) : loading ? (
            <div className="hp-skel" style={{ height: 64 }} />
          ) : null}
          <TextLink onClick={() => navigate('/history')}>{t('home.recent.cta')}</TextLink>
        </section>
      </div>

      {home?.agreement ? (
        <section className="hp-section">
          <Link to="/me/agreements" className="hp-listrow">
            <IdCardIcon size={18} />
            <span>
              {home.agreement.status === 'ACTIVE'
                ? home.agreement.coverageEndDate
                  ? t('home.agreement.active', {
                      date: formatDate(home.agreement.coverageEndDate, locale),
                    })
                  : t('home.agreement.activeNoDate')
                : home.agreement.status === 'EXPIRING' && home.agreement.coverageEndDate
                  ? t('home.agreement.expiring', {
                      date: formatDate(home.agreement.coverageEndDate, locale),
                    })
                  : t('home.agreement.none')}
            </span>
            <span className="hp-listrow__chev">›</span>
          </Link>
        </section>
      ) : null}
    </>
  );
}

function daypart(): 'morning' | 'afternoon' | 'evening' {
  const h = Number(
    new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Asia/Hong_Kong',
      hour: '2-digit',
      hour12: false,
    }).format(new Date()),
  );
  if (h < 12) return 'morning';
  if (h < 18) return 'afternoon';
  return 'evening';
}

function findShop(shops: Map<string, PortalPickupShop>, name: string): PortalPickupShop | null {
  for (const s of shops.values()) if (s.name === name) return s;
  return null;
}

function SituationCard({
  situation,
  shop,
  heroUrl,
  locale,
}: {
  situation: NonNullable<HomeResponse['situation']>;
  shop: PortalPickupShop | null;
  heroUrl: string | null;
  locale: string;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  if (situation.type === 'READY') {
    return (
      <section
        className={cn('hp-situation', !heroUrl && 'hp-situation--compact')}
        data-quality-id="portal.home.situation.ready"
      >
        {heroUrl ? (
          <div>
            <div className="hp-situation__photo">
              <img src={heroUrl} alt="" />
            </div>
            {shop ? (
              <p className="hp-caption hp-caption--italic hp-situation__caption">{shop.name}</p>
            ) : null}
          </div>
        ) : null}
        <div className="hp-situation__body">
          <p className="hp-label">{t('collect.detail.status.READY_FOR_PICKUP', { shop: '' })}</p>
          <p className="hp-situation__sentence">
            {t('home.situation.ready', { shop: situation.shopName })}
          </p>
          <div className="hp-situation__facts">
            {shop?.addressLines?.length ? (
              <FactRow icon={<LocationPinIcon size={18} />}>{shop.addressLines.join(', ')}</FactRow>
            ) : null}
            {shop?.openingHoursText ? (
              <FactRow icon={<InfoIcon size={18} />}>{shop.openingHoursText}</FactRow>
            ) : null}
            <FactRow icon={<IdCardIcon size={18} />}>{t('home.situation.readyId')}</FactRow>
          </div>
          <PrimaryButton onClick={() => navigate(`/collect/${situation.requestId}`)}>
            {t('home.situation.readyCta')}
          </PrimaryButton>
        </div>
      </section>
    );
  }

  if (situation.type === 'IN_PROGRESS') {
    return (
      <section className="hp-situation" data-quality-id="portal.home.situation.progress">
        <div className="hp-situation__body">
          <p className="hp-label">{situation.requestNumber}</p>
          <p className="hp-situation__sentence">{t('home.situation.progress')}</p>
          <PrimaryButton onClick={() => navigate(`/collect/${situation.requestId}`)}>
            {t('home.situation.progressCta')}
          </PrimaryButton>
        </div>
      </section>
    );
  }

  if (situation.type === 'AGREEMENT_EXPIRING') {
    return (
      <section className="hp-situation" data-quality-id="portal.home.situation.expiring">
        <div className="hp-situation__body">
          <p className="hp-label">
            <WarningIcon size={14} />
          </p>
          <p className="hp-situation__sentence">
            {t('home.situation.expiring', {
              date: formatDate(situation.coverageEndDate, locale),
            })}
          </p>
          <PrimaryButton onClick={() => navigate('/me/help')}>
            {t('home.situation.expiringCta')}
          </PrimaryButton>
        </div>
      </section>
    );
  }

  if (situation.type === 'WELCOME') {
    return (
      <section className="hp-situation" data-quality-id="portal.home.situation.welcome">
        <div className="hp-situation__body">
          <p className="hp-situation__sentence">{t('home.situation.welcome')}</p>
          <PrimaryButton onClick={() => navigate('/humidor')}>
            {t('home.situation.welcomeCta')}
          </PrimaryButton>
        </div>
      </section>
    );
  }
  return null;
}
